import {
  ImageElement,
  MarketingForgeElementType,
  defaultContentAlignment,
} from "@redotech/redo-content-builder-common/element-schema";
import ImageIcon from "@redotech/redo-icons/image-plus.svg";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext } from "react";
import { getBaseElementStyles } from "./base-element-styles";
import { SizedBoxContext } from "./element-box";
import * as ImageCSS from "./form-image.module.css";

export const FormImage = memo(function FormImage({
  element,
}: {
  element: Omit<ImageElement, "type"> & { type: string };
}) {
  const hasImage = element.url !== "";
  const baseStyles = getBaseElementStyles(element);
  // Each axis is native unless its toggle is explicitly off and a non-zero
  // size is set. Native means the container decides the width and the image
  // keeps its intrinsic height; an explicit size instead pins the box, which
  // is what stops an oversized upload from pushing the rest of the form out
  // of the viewport.
  const widthPx =
    element.useNativeWidth === false && element.widthSizing === undefined
      ? element.widthPx
      : undefined;
  const heightPx =
    element.useNativeHeight === false && element.heightSizing === undefined
      ? element.heightPx
      : undefined;
  const inSizedBox = useContext(SizedBoxContext) !== null;
  const alignment =
    element.contentAlignment ??
    defaultContentAlignment(MarketingForgeElementType.IMAGE);

  return (
    <Flex
      height="full"
      overflow={inSizedBox ? "hidden" : undefined}
      // design-allow: merchant-authored image styles and pixel sizes have no tokens
      style={{
        ...baseStyles,
        // The picture scales into whatever box it was given.
        ...(inSizedBox && { flex: "1 1 auto", minWidth: 0, minHeight: 0 }),
        ...(widthPx ? { width: `${widthPx}px`, maxWidth: "100%" } : {}),
        ...(heightPx ? { height: `${heightPx}px` } : {}),
      }}
      w="full"
    >
      {hasImage ? (
        <img
          // Empty string signals a decorative image to screen readers (WCAG)
          alt={element.altText ?? ""}
          src={element.url}
          // design-allow: object-fit and position have no props
          style={{
            width: "100%",
            height: "100%",
            objectFit: element.objectFit ?? "cover",
            objectPosition: `${alignment.horizontal} ${alignment.vertical}`,
            ...(element.cornerRadius !== undefined && {
              borderRadius: `${element.cornerRadius}px`,
            }),
          }}
        />
      ) : (
        <div className={ImageCSS.placeholder}>
          <ImageIcon />
          Add image
        </div>
      )}
    </Flex>
  );
});
